"use client";
import { useEffect, useState } from "react";
import { getHeroSection, HeroSectionExtended } from "@/actions/heroSection";
import { ArrowRightIcon, PlayIcon } from "@heroicons/react/24/outline";

const HeroSection = () => {
  const [hero, setHero] = useState<HeroSectionExtended | null>(null);

  useEffect(() => {
    async function fetchHero() {
      const res = await getHeroSection();
      if (res.success && res.heroSection) {
        setHero(res.heroSection);
      }
    }
    fetchHero();
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-700 to-purple-800"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* <div className="mb-8">
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-8">
            🚀 Иновативно решение за общините
          </span>
        </div> */}

        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
          {hero ? (
            <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
              {hero.title}
            </span>
          ) : (
            <>
              <span className="bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                Уеб портал за
              </span>
              <br />
              <span className="bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
                Общински съвети
              </span>
            </>
          )}
        </h1>

        <div className="text-xl sm:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
          {hero ? (
            <span
              className="text-blue-100"
              dangerouslySetInnerHTML={{ __html: hero.description }}
            />
          ) : (
            <>
              Представяме иновативен уеб портал, който свързва гражданите с техните общински съвети.
              <br className="hidden sm:block" />
              Тук можете да намерите информация за събития, решения и новини от вашата община.
              <br className="hidden sm:block" />
              <span className="text-blue-200 font-semibold">
                Присъединете се към нас и бъдете част от промяната в общността си!
              </span>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
          <a
            href="#contact"
            className="group inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-2xl hover:shadow-blue-500/25 hover:bg-blue-50 transition-all duration-300 text-lg"
          >
            Запитване за информация
            <ArrowRightIcon className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <button className="group inline-flex items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-lg backdrop-blur-sm">
            <PlayIcon className="mr-2 w-5 h-5" />
            Видео демонстрация
          </button>
        </div>

        {/* Floating cards with key features */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
            <div className="text-3xl mb-3">🏛️</div>
            <h3 className="text-white font-semibold mb-2">За общини</h3>
            <p className="text-blue-100 text-sm">Управление на съдържание и комуникация</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
            <div className="text-3xl mb-3">👥</div>
            <h3 className="text-white font-semibold mb-2">За граждани</h3>
            <p className="text-blue-100 text-sm">Лесен достъп до информация и услуги</p>
          </div>
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
            <div className="text-3xl mb-3">🔒</div>
            <h3 className="text-white font-semibold mb-2">Сигурност</h3>
            <p className="text-blue-100 text-sm">Защитени данни и безопасна платформа</p>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;